import { useCallback, useEffect, useMemo, useRef } from 'react'
import { interpolate } from '../../animation'
import { useUnmountEffect } from '../useUnmountEffect'

export const useWaveform = (audioContext, audioStream, options = {}) => {
  const {
    samples = 52,
    minAmplitude = 3,
    maxAmplitude = 30,
    bufferSize = 256,
    inactiveColor = '#ECECF3',
    activeColor = '#AC0AE8',
    easing = num => num
  } = options

  const normalizedSamples = useMemo(
    () => (samples % 2 === 0 ? samples - 1 : samples),
    [samples]
  )
  const frequencyBandArray = useMemo(
    () => [...Array(normalizedSamples).keys()],
    [normalizedSamples]
  )
  const audioAnalyserRef = useRef(null)
  const animationFrameIdRef = useRef(null)
  const containerRef = useRef(null)
  const waveformBarsRef = useRef([])

  useEffect(() => {
    if (!containerRef.current) return

    waveformBarsRef.current = containerRef.current.querySelectorAll(
      '[data-waveform-bar]'
    )
  }, [])

  const initializeAudioAnalyser = useCallback(() => {
    if (!audioContext || !audioStream) return false
    if (audioAnalyserRef.current) return true

    const analyser = audioContext.createAnalyser()
    analyser.fftSize = bufferSize
    audioStream.connect(analyser)
    audioAnalyserRef.current = analyser

    return true
  }, [audioContext, audioStream, bufferSize])

  const withFrequencyData = useCallback(
    callbackFn => {
      const bufferLength = audioAnalyserRef.current?.frequencyBinCount
      const amplitudeArray = new Uint8Array(bufferLength)
      audioAnalyserRef.current?.getByteFrequencyData(amplitudeArray)

      const visualization = amplitudeArray.slice(8, 8 + Math.floor(samples / 2))
      const mirrored = [...visualization.slice(1).reverse(), ...visualization]

      callbackFn(mirrored)
    },
    [samples]
  )

  const adjustFreqBandStyle = useCallback(
    newAmplitudeData => {
      waveformBarsRef.current.forEach((bar, index) => {
        const height = interpolate(
          newAmplitudeData[index],
          [0, 255],
          [0, maxAmplitude],
          { easing }
        )
        const bgColor = height > minAmplitude ? activeColor : inactiveColor
        bar.style.height = `${height}px`
        bar.style.background = bgColor
      })
    },
    [activeColor, easing, inactiveColor, maxAmplitude, minAmplitude]
  )

  const animateOneFrame = useCallback(() => {
    withFrequencyData(adjustFreqBandStyle)
    animationFrameIdRef.current = requestAnimationFrame(animateOneFrame)
  }, [adjustFreqBandStyle, withFrequencyData])

  const start = useCallback(() => {
    const initiated = initializeAudioAnalyser()

    if (initiated) {
      animationFrameIdRef.current = requestAnimationFrame(animateOneFrame)
    }
  }, [animateOneFrame, initializeAudioAnalyser])

  const stop = useCallback(() => {
    if (audioStream && audioAnalyserRef.current)
      audioStream.disconnect(audioAnalyserRef.current)

    if (animationFrameIdRef.current) {
      cancelAnimationFrame(animationFrameIdRef.current)
      animationFrameIdRef.current = null
    }

    audioAnalyserRef.current = null

    waveformBarsRef.current.forEach(bar => {
      bar.style.height = `${minAmplitude}px`
      bar.style.background = inactiveColor
    })
  }, [audioStream, minAmplitude, inactiveColor])

  useUnmountEffect(stop, [])

  return {
    waveFormContainerRef: containerRef,
    bars: frequencyBandArray,
    beginAnimation: start,
    endAnimation: stop
  }
}
